import { useState, useEffect, useCallback, useRef } from "react";
import * as contractApi from "../services/contractApi";

/**
 * Custom hook for managing contract data
 *
 * - Fetches contract overview (editable flag, exhibitor info) on mount
 * - Loads step data lazily when a step is navigated to
 * - Provides save method for auto-save and manual save
 * - Handles submission with server-side validation
 * - Contact info (step 10) uses separate global endpoints
 */
// Steps that have form data (not informational) — defined at module level
// so useCallback dependency arrays don't need to include them
const FORM_STEPS = [3, 5, 6, 7, 8, 9];
const CONTACT_STEP = 10;

export const useContractData = (contractId, { isAdmin = false } = {}) => {
  const [contract, setContract] = useState(null);
  const [stepData, setStepData] = useState({});
  const [loading, setLoading] = useState(true);
  const [stepLoading, setStepLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});

  // Track which steps have been loaded to avoid redundant fetches
  const loadedStepsRef = useRef(new Set());

  // Fetch contract overview on mount
  useEffect(() => {
    const fetchContract = async () => {
      if (!contractId) return;
      setLoading(true);
      setError(null);
      try {
        const data = await contractApi.getContract(contractId);
        setContract(data);
      } catch (err) {
        setError(err.response?.data?.message || err.message || "Failed to load contract");
      } finally {
        setLoading(false);
      }
    };
    fetchContract();
  }, [contractId]);

  // Load step data for a specific step
  const loadStepData = useCallback(async (step) => {
    if (!contractId) return;

    // Skip if already loaded
    if (loadedStepsRef.current.has(step)) return;

    setStepLoading(true);
    try {
      let data;
      if (step === CONTACT_STEP) {
        data = isAdmin
          ? await contractApi.getContactInfoForContract(contractId)
          : await contractApi.getContactInfo();
      } else if (FORM_STEPS.includes(step)) {
        data = await contractApi.getStepData(contractId, step);
      } else {
        // Informational steps (1, 2, 4) — no data to fetch
        setStepLoading(false);
        return;
      }

      setStepData((prev) => ({ ...prev, [step]: data }));
      loadedStepsRef.current.add(step);
    } catch (err) {
      // If 404, treat as empty data (first time)
      if (err.response?.status === 404) {
        setStepData((prev) => ({ ...prev, [step]: {} }));
        loadedStepsRef.current.add(step);
      } else {
        setError(err.response?.data?.message || `Failed to load step ${step} data`);
      }
    } finally {
      setStepLoading(false);
    }
  }, [contractId, isAdmin]);

  // Save step data
  const saveStep = useCallback(async (step, data) => {
    if (!contractId || !contract?.editable) return;

    setSaving(true);
    try {
      if (step === CONTACT_STEP) {
        if (isAdmin) {
          await contractApi.saveContactInfoForContract(contractId, data);
        } else {
          await contractApi.saveContactInfo(data);
        }
      } else {
        await contractApi.saveStepData(contractId, step, data);
      }

      // Update local cache
      setStepData((prev) => ({ ...prev, [step]: data }));
    } catch (err) {
      console.error(`Failed to save step ${step}:`, err);
      throw err;
    } finally {
      setSaving(false);
    }
  }, [contractId, contract?.editable, isAdmin]);

  // Submit contract
  const submitContract = useCallback(async () => {
    if (!contractId) return;
    setSaving(true);
    setValidationErrors({});

    try {
      const result = await contractApi.submitContract(contractId);

      if (!result.success && result.errors) {
        setValidationErrors(result.errors);
        return result;
      }

      return result;
    } catch (err) {
      // Handle 422 validation errors from server
      if (err.response?.status === 422 && err.response?.data?.errors) {
        setValidationErrors(err.response.data.errors);
        return { success: false, errors: err.response.data.errors };
      }
      setError(err.response?.data?.message || "Failed to submit contract");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [contractId]);

  // Generate PDF
  const generatePDF = useCallback(async () => {
    if (!contractId) return;
    setSaving(true);
    try {
      const pdfBlob = await contractApi.generateContractPDF(contractId);
      const url = window.URL.createObjectURL(pdfBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `contract-${contractId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      return pdfBlob;
    } catch (err) {
      setError(err.response?.data?.message || "Failed to generate PDF");
      throw err;
    } finally {
      setSaving(false);
    }
  }, [contractId]);

  // Force reload a step's data (useful after save conflicts)
  const reloadStep = useCallback((step) => {
    loadedStepsRef.current.delete(step);
    return loadStepData(step);
  }, [loadStepData]);

  return {
    contract,
    stepData,
    loading,
    stepLoading,
    saving,
    error,
    validationErrors,

    loadStepData,
    saveStep,
    submitContract,
    generatePDF,
    reloadStep,

    clearError: () => setError(null),
    clearValidationErrors: () => setValidationErrors({}),
  };
};

export default useContractData;
